import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AdvisorDashboard from './components/advisor/AdvisorDashboard';
import ClientDashboard from './components/client/ClientDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import AdvisorApplication from './components/advisor/AdvisorApplication';
import LandingPage from './components/LandingPage';
import SignUp from './components/auth/SignUp';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Horoscope from './components/Horoscope';
import PalmReading from './components/PalmReading';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import Advisors from './components/Advisors';
import Navigation from './components/Navigation';
import Blog from './components/Blog';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VoiceCallProvider } from './context/VoiceCallContext';
import { supabase } from './lib/supabase';
import StripeWebhookHandler from './components/StripeWebhookHandler';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import Footer from './components/Footer';
import ChatScreen from './components/chat/ChatScreen';
import PastConversationViewer from './components/client/PastConversationViewer';
import AuthRedirectHandler from './components/auth/AuthRedirectHandler';
import { useActivityTracker } from './hooks/useActivityTracker';

// Protected route component that handles routing based on user type and admin status
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('admin_user')
          .eq('id', user.id)
          .single();

        setIsAdmin(profile?.admin_user || false);
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking user status:', error);
        setIsLoading(false);
      }
    };

    checkUserStatus();
  }, [user]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Component to check for active chats and redirect if needed
const ActiveChatChecker = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useActivityTracker();

  useEffect(() => {
    // Skip the check if we're already on a chat page or not on the landing page
    if (!user || 
        location.pathname.includes('/chat/') || 
        location.pathname !== '/' ||
        location.pathname.includes('/login') ||
        location.pathname.includes('/signup')) {
      setIsChecking(false);
      return;
    }

    const checkForActiveChats = async () => {
      try {
        // Check if the user has any active chats
        const { data, error } = await supabase
          .from('conversations')
          .select(`
            id,
            client_id,
            advisor_id,
            status
          `)
          .eq('client_id', user.id)
          .eq('status', 'active')
          .order('updated_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error checking for active chats:', error);
          setIsChecking(false);
          return;
        }

        // If there's an active chat, redirect to it
        if (data && data.length > 0) {
          console.log('Found active chat, redirecting to:', data[0].id);
          navigate(`/chat/${data[0].id}`);
        }
      } catch (err) {
        console.error('Error in active chat check:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkForActiveChats();
  }, [user, navigate, location.pathname]);

  // Return null as this is just a utility component
  return isChecking ? <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
  </div> : null;
};

function App() {
  return (
    <AuthProvider>
      <VoiceCallProvider>
        <Router>
          <AuthRedirectHandler>
            <div className="min-h-screen flex flex-col">
              <Navigation />
              <ActiveChatChecker />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/advisors" element={<Advisors />} />
                <Route path="/horoscopes" element={<Horoscope />} />
                <Route path="/palm-reading" element={<PalmReading />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/advisor/apply" element={<AdvisorApplication />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/advisor/dashboard" 
                  element={
                    <ProtectedRoute>
                      <AdvisorDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/client/dashboard" 
                  element={
                    <ProtectedRoute>
                      <ClientDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/chat/:conversationId" 
                  element={
                    <ProtectedRoute>
                      <ChatScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/past-conversation/:conversationId" 
                  element={
                    <ProtectedRoute>
                      <PastConversationViewer />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/stripe-webhook" element={<StripeWebhookHandler />} />
                <Route path="*" element={<Navigate to="/\" replace />} />
              </Routes>
            </main>
            <Footer />
            </div>
          </AuthRedirectHandler>
        </Router>
      </VoiceCallProvider>
    </AuthProvider>
  );
}

export default App;